"use client";

import { useEffect, useRef, useState } from "react";

export type Review = { name: string; when: string; text: string[] };

// One Google review card: text clamped to 4 lines, "Weiterlesen" opens it (only shown when the text is cut).
export function ReviewCard({ name, when, text }: Review) {
  const [open, setOpen] = useState(false);
  const [cut, setCut] = useState(true);
  const ref = useRef<HTMLQuoteElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      if (!el.parentElement?.classList.contains("rev-open")) setCut(el.scrollHeight > el.clientHeight + 1);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <figure
      className={`m-0 flex flex-col gap-2 rounded-[10px] border border-line bg-surface p-4 min-[640px]:gap-4 min-[640px]:p-7 ${open ? "rev-open" : ""}`}
    >
      <div className="flex items-center justify-between gap-3">
        <span role="img" aria-label="5 von 5 Sternen" className="text-base tracking-[2px] text-[#B8860B] min-[640px]:text-lg">
          ★★★★★
        </span>
        {/* phones: avatar and date next to the stars; the name stays for screen readers */}
        <span className="flex items-center gap-2 text-sm text-ink-3 min-[640px]:hidden">
          <span aria-hidden="true" className="flex size-7 flex-none items-center justify-center rounded-full bg-muted text-sm font-semibold text-ink">
            {name[0]}
          </span>
          <span className="sr-only">{name}, </span>
          {when} · Google
        </span>
      </div>
      <div className="relative">
        <blockquote ref={ref} className="rev-text m-0 text-base leading-[1.6] text-ink-2">
          „
          {text.map((t, i) => (
            <span key={i}>
              {i > 0 && <br />}
              {t}
            </span>
          ))}
          “
        </blockquote>
        {/* phones: an arrow at the end of the cut text instead of "Weiterlesen" (saves a line) */}
        {(cut || open) && (
          <button
            type="button"
            aria-label={open ? "Weniger anzeigen" : "Weiterlesen"}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className={`text-accent min-[640px]:hidden ${open ? "ml-2" : "absolute right-0 bottom-0 bg-surface pl-6"} font-semibold`}
          >
            {open ? "↑" : "→"}
          </button>
        )}
      </div>
      {(cut || open) && (
        <button type="button" className="rev-more hidden min-h-11 text-accent min-[640px]:block" aria-expanded={open} onClick={() => setOpen(!open)}>
          {open ? "Weniger anzeigen" : "Weiterlesen"}
        </button>
      )}
      <figcaption className="mt-auto hidden items-center gap-3 pt-1 min-[640px]:flex">
        <span
          aria-hidden="true"
          className="flex size-10 flex-none items-center justify-center rounded-full bg-muted text-[17px] font-semibold"
        >
          {name[0]}
        </span>
        <div className="flex flex-col leading-[1.3]">
          <strong className="font-semibold">{name}</strong>
          <span className="text-sm text-ink-3">{when} · Google</span>
        </div>
      </figcaption>
    </figure>
  );
}
